import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Plus, Trash2, Pencil, X, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("dynamo_token")}` });

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAY_ORDER = Object.fromEntries(DAYS.map((d, i) => [d, i]));

const EMPTY_FORM = {
  category: "Recreational",
  day: "Tuesday",
  start_time: "09:00",
  end_time: "10:00",
  price: "",
  age_min_is_school_age: false,
  age_min_years: "0",
  age_min_months: "0",
  age_max_years: "0",
  age_max_months: "0",
  spaces: "",
};

// "09:30" → "9:30am"
function fmt12(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "pm" : "am";
  const h12 = h % 12 || 12;
  return m === 0 ? `${h12}${ampm}` : `${h12}:${String(m).padStart(2, "0")}${ampm}`;
}

function ageLabel(yrs, mths) {
  const parts = [];
  if (Number(yrs) > 0) parts.push(`${yrs}yr`);
  if (Number(mths) > 0) parts.push(`${mths}m`);
  return parts.join(" ") || "0m";
}

function minAgeDisplay(cls) {
  return cls.age_min_is_school_age ? "School Age" : ageLabel(cls.age_min_years, cls.age_min_months);
}

// ── Form ──────────────────────────────────────────────────────────────────────
function ClassForm({ initial = EMPTY_FORM, onSave, onCancel, saving }) {
  const [f, setF] = useState(initial);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const valid = f.category && f.day && f.start_time && f.end_time && f.price !== "" && f.spaces !== "";

  return (
    <form
      data-testid="class-form"
      onSubmit={e => { e.preventDefault(); if (valid) onSave(f); }}
      className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-4"
    >
      {/* Row 1: Category, Day, Start, End */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Type</label>
          <select
            data-testid="class-category-select"
            value={f.category}
            onChange={e => set("category", e.target.value)}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white
                       focus:outline-none focus:ring-2 focus:ring-[#1656a4] focus:border-transparent"
          >
            <option value="Recreational">Recreational</option>
            <option value="Gymini">Gymini</option>
            <option value="Gymtots">Gymtots</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Day</label>
          <select
            data-testid="class-day-select"
            value={f.day}
            onChange={e => set("day", e.target.value)}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white
                       focus:outline-none focus:ring-2 focus:ring-[#1656a4] focus:border-transparent"
          >
            {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Start time</label>
          <input
            data-testid="class-start-time"
            type="time"
            value={f.start_time}
            onChange={e => set("start_time", e.target.value)}
            required
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md
                       focus:outline-none focus:ring-2 focus:ring-[#1656a4] focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">End time</label>
          <input
            data-testid="class-end-time"
            type="time"
            value={f.end_time}
            onChange={e => set("end_time", e.target.value)}
            required
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md
                       focus:outline-none focus:ring-2 focus:ring-[#1656a4] focus:border-transparent"
          />
        </div>
      </div>

      {/* Row 2: Price, Min age, Max age, Spaces */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-start">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Price (£)</label>
          <input
            data-testid="class-price-input"
            type="number" min="0" step="0.01"
            value={f.price}
            onChange={e => set("price", e.target.value)}
            placeholder="e.g. 8.50"
            required
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md
                       focus:outline-none focus:ring-2 focus:ring-[#1656a4] focus:border-transparent"
          />
        </div>

        {/* Min age */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Min age</label>
          <button
            type="button"
            data-testid="school-age-toggle"
            onClick={() => set("age_min_is_school_age", !f.age_min_is_school_age)}
            className={`w-full mb-2 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors
              ${f.age_min_is_school_age
                ? "bg-[#1656a4] text-white border-[#1656a4]"
                : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"}`}
          >
            {f.age_min_is_school_age ? "✓ School Age" : "Set as School Age"}
          </button>
          {!f.age_min_is_school_age && (
            <div className="flex gap-2">
              <div className="flex-1">
                <input
                  data-testid="class-age-min-years"
                  type="number" min="0" max="18"
                  value={f.age_min_years}
                  onChange={e => set("age_min_years", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md
                             focus:outline-none focus:ring-2 focus:ring-[#1656a4] focus:border-transparent"
                />
                <span className="text-xs text-slate-400 mt-0.5 block text-center">yrs</span>
              </div>
              <div className="flex-1">
                <input
                  data-testid="class-age-min-months"
                  type="number" min="0" max="11"
                  value={f.age_min_months}
                  onChange={e => set("age_min_months", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md
                             focus:outline-none focus:ring-2 focus:ring-[#1656a4] focus:border-transparent"
                />
                <span className="text-xs text-slate-400 mt-0.5 block text-center">mths</span>
              </div>
            </div>
          )}
        </div>

        {/* Max age */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Max age</label>
          <div className="flex gap-2">
            <div className="flex-1">
              <input
                data-testid="class-age-max-years"
                type="number" min="0" max="18"
                value={f.age_max_years}
                onChange={e => set("age_max_years", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md
                           focus:outline-none focus:ring-2 focus:ring-[#1656a4] focus:border-transparent"
              />
              <span className="text-xs text-slate-400 mt-0.5 block text-center">yrs</span>
            </div>
            <div className="flex-1">
              <input
                data-testid="class-age-max-months"
                type="number" min="0" max="11"
                value={f.age_max_months}
                onChange={e => set("age_max_months", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md
                           focus:outline-none focus:ring-2 focus:ring-[#1656a4] focus:border-transparent"
              />
              <span className="text-xs text-slate-400 mt-0.5 block text-center">mths</span>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Taster spaces</label>
          <input
            data-testid="class-spaces-input"
            type="number" min="0"
            value={f.spaces}
            onChange={e => set("spaces", e.target.value)}
            placeholder="e.g. 4"
            required
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md
                       focus:outline-none focus:ring-2 focus:ring-[#1656a4] focus:border-transparent"
          />
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <button
          data-testid="class-save-button"
          type="submit"
          disabled={!valid || saving}
          className="flex items-center gap-1.5 bg-[#1656a4] hover:bg-[#124687] text-white
                     text-sm font-medium px-4 py-2 rounded-md transition-colors disabled:opacity-50"
        >
          <Check size={14} />
          {saving ? "Saving…" : "Save class"}
        </button>
        <button
          data-testid="class-cancel-button"
          type="button"
          onClick={onCancel}
          className="flex items-center gap-1.5 text-slate-500 hover:text-slate-700
                     text-sm px-4 py-2 rounded-md transition-colors border border-slate-200 hover:border-slate-300"
        >
          <X size={14} />
          Cancel
        </button>
      </div>
    </form>
  );
}

// ── Main tab ──────────────────────────────────────────────────────────────────
export default function ClassesTab() {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchClasses(); }, []);

  async function fetchClasses() {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/classes`, { headers: auth() });
      setClasses(data.data);
    } catch {
      toast.error("Failed to load classes");
    } finally {
      setLoading(false);
    }
  }

  function coerce(f) {
    return {
      ...f,
      price: parseFloat(f.price),
      age_min_years: parseInt(f.age_min_years),
      age_min_months: parseInt(f.age_min_months),
      age_max_years: parseInt(f.age_max_years),
      age_max_months: parseInt(f.age_max_months),
      spaces: parseInt(f.spaces),
    };
  }

  async function handleAdd(f) {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/classes`, coerce(f), { headers: auth() });
      setClasses(p => [...p, data]);
      setShowAdd(false);
      toast.success(`${data.category || "Class"} (${data.day}) added`);
    } catch {
      toast.error("Failed to add class");
    } finally {
      setSaving(false);
    }
  }

  async function handleEdit(id, f) {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/classes/${id}`, coerce(f), { headers: auth() });
      setClasses(p => p.map(c => c.id === id ? data : c));
      setEditId(null);
      toast.success(`${data.category || "Class"} (${data.day}) updated`);
    } catch {
      toast.error("Failed to update class");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(cls) {
    if (!window.confirm(`Delete ${cls.category || "this class"} (${cls.day})?`)) return;
    try {
      await axios.delete(`${API}/classes/${cls.id}`, { headers: auth() });
      setClasses(p => p.filter(c => c.id !== cls.id));
      toast.success(`${cls.category || "Class"} (${cls.day}) deleted`);
    } catch {
      toast.error("Failed to delete class");
    }
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div>
          <h2 className="text-base font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
            Classes
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            {loading ? "Loading…" : `${classes.length} class${classes.length === 1 ? "" : "es"}`}
          </p>
        </div>
        {!showAdd && (
          <button
            data-testid="add-class-button"
            onClick={() => { setShowAdd(true); setEditId(null); }}
            className="flex items-center gap-1.5 bg-[#1656a4] hover:bg-[#124687]
                       text-white text-sm font-medium px-3.5 py-2 rounded-md transition-colors"
          >
            <Plus size={14} />
            Add class
          </button>
        )}
      </div>

      <div className="p-5 space-y-3">
        {showAdd && (
          <ClassForm onSave={handleAdd} onCancel={() => setShowAdd(false)} saving={saving} />
        )}

        {loading && (
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
            ))}
          </div>
        )}

        {!loading && classes.length === 0 && !showAdd && (
          <div className="py-12 text-center text-slate-400 text-sm">
            No classes yet. Click "Add class" to get started.
          </div>
        )}

        {!loading && [...classes].sort((a, b) => {
          const dayDiff = (DAY_ORDER[a.day] ?? 99) - (DAY_ORDER[b.day] ?? 99);
          if (dayDiff !== 0) return dayDiff;
          return (a.start_time || "").localeCompare(b.start_time || "");
        }).map(cls => (
          <div key={cls.id} data-testid={`class-item-${cls.id}`}>
            {editId === cls.id ? (
              <ClassForm
                initial={{
                  category: cls.category || "Recreational",
                  day: cls.day || "Tuesday",
                  start_time: cls.start_time || "09:00",
                  end_time: cls.end_time || "10:00",
                  price: String(cls.price),
                  age_min_is_school_age: cls.age_min_is_school_age || false,
                  age_min_years: String(cls.age_min_years ?? 0),
                  age_min_months: String(cls.age_min_months ?? 0),
                  age_max_years: String(cls.age_max_years ?? 0),
                  age_max_months: String(cls.age_max_months ?? 0),
                  spaces: String(cls.spaces),
                }}
                onSave={f => handleEdit(cls.id, f)}
                onCancel={() => setEditId(null)}
                saving={saving}
              />
            ) : (
              <div className="flex items-center justify-between gap-4 px-4 py-3.5
                              border border-slate-200 rounded-lg hover:border-slate-300
                              hover:bg-slate-50 transition-colors group">
                <div className="flex-1 min-w-0 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-1">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{cls.category || "Recreational"}</p>
                    <p className="text-xs text-slate-400">{cls.day}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-700">
                      {cls.day} {fmt12(cls.start_time)} – {fmt12(cls.end_time)}
                    </p>
                    <p className="text-xs text-slate-400">Time</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-700">£{Number(cls.price).toFixed(2)}</p>
                    <p className="text-xs text-slate-400">Price</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-700">
                      {minAgeDisplay(cls)} – {ageLabel(cls.age_max_years, cls.age_max_months)}
                    </p>
                    <p className="text-xs text-slate-400">Age range</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-700">{cls.spaces}</p>
                    <p className="text-xs text-slate-400">Taster spaces</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  <button
                    data-testid={`edit-class-${cls.id}`}
                    onClick={() => { setEditId(cls.id); setShowAdd(false); }}
                    className="p-1.5 text-slate-400 hover:text-[#1656a4] rounded transition-colors"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    data-testid={`delete-class-${cls.id}`}
                    onClick={() => handleDelete(cls)}
                    className="p-1.5 text-slate-400 hover:text-[#ff3131] rounded transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
